import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	IconButton,
	CircularProgress,
	Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentReturn from "@mui/icons-material/AssignmentReturn";
import certificationSchema from "../../Training/Certifications/certificationSchema";
import { getCertificationFields } from "../../Training/Certifications/certificationFields";
import FormSection from "../../../components/Form/FormSection";
import {
	createTrainingCertification,
	updateTrainingCertification,
} from "../../../reducers/TrainingCertificationReducer";
import { getService } from "../../../reducers/service";
import { formatSaveDate } from "../../../reducers/TimeReducer";
import { notify } from "../../../reducers/NotifierReducer";
import { serviceNames, entityNamesForCustomId } from "../../../utils/constants";
import { getEmployeeOptionLabel } from "../../../utils/functions";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import moment from "moment";
import { useTheme } from "@emotion/react";

const buildDefaults = (data, employeeNum = "") => ({
	id: data?.id || null,
	certificationType: data?.certificationType || "",
	dateObtained: data?.dateObtained
		? moment(data.dateObtained).format("MM/DD/YYYY")
		: "",
	expireDate: data?.expireDate
		? moment(data.expireDate).format("MM/DD/YYYY")
		: "",
	employee: data?.employee || employeeNum || "",
	fromWhom: data?.fromWhom || "",
	comments: data?.comments || "",
	certificateNumber: data?.certificateNumber || "",
});

const AddEditCertificationDialog = ({
	open,
	initialData,
	employeeNum,
	onClose,
	onSave,
}) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const user = useSelector((state) => state.user);
	const id = initialData?.id;
	const userAgency = useSelector((state) => state.user.userAgency);

	const [loaded, setLoaded] = useState(false);
	const [employees, setEmployees] = useState([]);
	const [certificationTypeOptions, setCertificationTypeOptions] = useState(
		[],
	);

	const defaultValues = useMemo(
		() => buildDefaults(initialData, employeeNum),
		[initialData, employeeNum],
	);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(certificationSchema),
		defaultValues,
	});

	const loadOptions = async () => {
		setLoaded(false);
		try {
			const services = {
				employees: getService(serviceNames.personnel),
				distinctService: getService(
					serviceNames.perTrainingCertificationDistinct,
				),
			};

			const results = await Promise.all([
				services.employees.find({
					query: {
						$limit: -1,
						isDeleted: false,
						$sort: { lastName: 1, firstName: 1, middleName: 1 },
						agencyId: userAgency,
					},
				}),
				services.distinctService.find({
					query: {
						column: ["certificationType"],
						agencyId: userAgency,
					},
				}),
			]);

			setEmployees(results[0]);
			setCertificationTypeOptions(results[1].certificationType);
			setLoaded(true);
		} catch (e) {
			setLoaded(true);
		}
	};

	useEffect(() => {
		if (open) loadOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		if (open && initialData) {
			reset(buildDefaults(initialData, employeeNum));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialData, employeeNum, open, reset]);

	useEffect(() => {
		if (open) {
			reset(defaultValues);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, defaultValues]);

	useEffect(() => {
		if (user.userAgency) {
			setValue("certificateNumber", "");
		}
	}, [user.userAgency, setValue]);

	useEffect(() => {
		if (open && !id && employeeNum) {
			setValue("employee", employeeNum);
		}
	}, [open, id, employeeNum, setValue]);

	const options = useMemo(
		() => ({
			employee: employees.map((emp) => ({
				label: getEmployeeOptionLabel(emp),
				value: emp.employeeNum,
			})),
			certificationType: certificationTypeOptions.map((type) => ({
				label: type,
				value: type,
			})),
		}),
		[employees, certificationTypeOptions],
	);

	const generateCustomId = async () => {
		const data = {
			EntityName: entityNamesForCustomId.CertificateId,
			agencyID: user.userAgency,
		};
		const service = getService(serviceNames.customId);
		service.timeout = 20000;

		const generatedId = await service.create(data);
		if (generatedId?.includes("Agency ID does not match")) {
			dispatch(
				notify(
					"Unable to generate Certificate ID because the Agency ID does not match.",
					"warning",
				),
			);
			return null;
		}

		return generatedId;
	};

	const handleAutoGenerateClick = async () => {
		const generatedId = await generateCustomId();
		if (generatedId) {
			setValue("certificateNumber", generatedId);
		}
	};

	const onSubmit = async (data) => {
		const certificateId =
			data?.certificateNumber?.trim() || (await generateCustomId());

		const cleanedData = {
			certificationType: data?.certificationType.trim() || null,
			dateObtained: data?.dateObtained
				? formatSaveDate(data?.dateObtained)
				: null,
			expireDate: data?.expireDate
				? formatSaveDate(data?.expireDate)
				: null,
			certificateNumber: certificateId,
			employee: data?.employee || null,
			fromWhom: data?.fromWhom || null,
			comments: data?.comments?.trim() || null,
			agencyId: user?.userAgency,
		};

		if (data?.id) {
			await dispatch(updateTrainingCertification(data?.id, cleanedData));
		} else {
			await dispatch(createTrainingCertification(cleanedData));
		}

		onSave?.();
		onClose?.();
	};

	const sectionProps = {
		control,
		errors,
		options,
		watch,
	};

	if (!open) {
		return null;
	}

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Typography variant="h6">
					{id ? "Edit Certification" : "Add Certification"}
				</Typography>
				<IconButton aria-label="close" onClick={onClose} size="large">
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				{!loaded ? (
					<Box
						display="flex"
						justifyContent="center"
						alignItems="center"
						minHeight={200}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box sx={{ pt: 1 }}>
						<FormSection
							title="Certification Information"
							icon={null}
							fields={getCertificationFields()}
							isExpandFunctionality={false}
							editPermission={true}
							theme={theme}
							isEdit={!!id}
							renderIdGenerationButton={() => (
								<Box>
									<Tooltip title="Auto generate certificate number">
										<IconButton
											color="primary"
											aria-label="Auto generate certificate number"
											size="large"
											onClick={handleAutoGenerateClick}
										>
											<AssignmentReturn />
										</IconButton>
									</Tooltip>
								</Box>
							)}
							{...sectionProps}
						/>
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATCloseButton onClick={onClose} tooltipText="Close" />
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					tooltipText="Save Certification"
				/>
			</DialogActions>
		</Dialog>
	);
};

export default AddEditCertificationDialog;
